import { Component, Inject } from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import {
  AddCatalogItemsDialogData,
  CatalogItemUnion,
} from '@components/add-catalog-items-dialog/1.0';
import { BadgeType } from '@constants/badge/1.0';
import { ButtonStyle } from '@constants/button/1.0';
import { MessageType } from '@constants/message/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { ItemListComponent } from '../../item-list/1.0/item-list.component';
import { ButtonComponent } from '../../button/1.0/button.component';
import { NgIf, NgFor } from '@angular/common';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BadgeComponent } from '../../badge/1.0/badge.component';
import { MessageComponent } from '../../message/1.0/message.component';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';

const ALL_TYPES = '__all__';

@Component({
  selector: 'txp-add-catalog-items-dialog',
  templateUrl: './add-catalog-items-dialog.component.html',
  styleUrls: ['./add-catalog-items-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    ItemListComponent,
    ButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatSelect,
    MatOption,
    NgFor,
    BadgeComponent,
    MessageComponent,
    MatDialogActions,
    TranslateModule,
  ],
})
export class AddCatalogItemsDialogComponent {
  badgeType = BadgeType; // expose enum to template
  buttonStyle = ButtonStyle;
  messageType = MessageType;
  warningMessage = '';
  noneSelectedMsg: string;
  filterText: string;
  form: UntypedFormGroup;
  entitiesSelectedText: string;
  selectedItems: CatalogItemUnion[] = [];
  stagedSelectedItems: CatalogItemUnion[];
  itemsList: CatalogItemUnion[];
  disabledItems: CatalogItemUnion[];
  disabledLabel: string;
  typeFilterValue = ALL_TYPES;
  allTypesValue = ALL_TYPES;

  /** Unfiltered list of selectable items. `itemsList` is always derived from this. */
  private masterItemsList: CatalogItemUnion[] = [];

  constructor(
    private dialogRef: MatDialogRef<AddCatalogItemsDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: AddCatalogItemsDialogData,
  ) {
    // copy the array because we will be manipulating it
    this.selectedItems = data.sortByName
      ? this.sortItemsByName(data.selectedList.slice())
      : data.selectedList.slice();
    const activeItems = data.fullList.filter(
      (item) => !(item as { isSoftDeleted?: boolean }).isSoftDeleted,
    );
    this.masterItemsList = this.removeFromList(this.selectedItems, activeItems);
    this.itemsList = this.applyTypeFilter();
    this.disabledItems = data.disabledList;
    this.disabledLabel = data.disabledLabel;
    this.refreshDisabledItems();

    // setup translations
    this.entitiesSelectedText = `${data.translations.type.toLowerCase()} ${translateService.instant(
      'menu.common.selected',
    )}`;
    this.noneSelectedMsg = translateService
      .instant('menu.common.noSelectedMsg')
      .replace(':items', data.translations.type);
    this.filterText = `${translateService.instant('menu.common.search')} ${data.translations.type}`;
    this.form = this.formBuilder.group({
      groupName: new UntypedFormControl(this.data.groupName),
    });
  }

  handleCancel() {
    this.dialogRef.close();
  }

  handleDone() {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    this.dialogRef.close({
      items: this.selectedItems,
      groupName: this.form.get('groupName').value,
    });
  }

  onSelected(selected: CatalogItemUnion[]) {
    this.stagedSelectedItems = selected;
    this.refreshDisabledItems();
  }

  addSelected() {
    if (this.stagedSelectedItems) {
      const nextSelected = this.selectedItems.concat(this.stagedSelectedItems);
      this.selectedItems = this.data.sortByName ? this.sortItemsByName(nextSelected) : nextSelected;
      this.masterItemsList = this.removeFromList(this.stagedSelectedItems, this.masterItemsList);
      this.itemsList = this.applyTypeFilter();
      this.stagedSelectedItems = [];
      this.refreshDisabledItems();
    }
  }

  onTypeFilterChange(value: string) {
    this.typeFilterValue = value;
    this.itemsList = this.applyTypeFilter();
  }

  private applyTypeFilter(): CatalogItemUnion[] {
    const typeKey = this.data.itemTypeField;

    if (!typeKey || !this.data.typeFilterOptions?.length || this.typeFilterValue === ALL_TYPES) {
      return this.masterItemsList.slice();
    }

    return this.masterItemsList.filter((item) => item?.[typeKey] === this.typeFilterValue);
  }

  private refreshDisabledItems() {
    if (!this.data.getDisabledList) {
      return;
    }

    const context = {
      selectedItems: this.selectedItems,
      stagedItems: this.stagedSelectedItems || [],
      availableItems: this.masterItemsList,
    };
    this.disabledItems = this.data.getDisabledList(context);
    this.warningMessage = this.data.getWarningMessage?.(context) || '';
  }

  isHeroProductGroup = (item: CatalogItemUnion) =>
    'isHeroProductGroup' in item && item.isHeroProductGroup;

  /**
   * Removes all items in removeList from masterList without changing masterList. Returns
   * a new array without the removed items
   *
   * @param removeList - array of items to remove
   * @param masterList - array of items to remove from
   */
  removeFromList(removeList: any[], masterList: any[]) {
    // don't modify the original array
    const newList = masterList.slice();
    for (const item of removeList) {
      const index = newList.indexOf(item);
      if (index > -1) {
        newList.splice(index, 1);
      }
    }
    return this.sortItemsByName(newList);
  }

  handleDelete(item: CatalogItemUnion) {
    this.selectedItems.splice(this.selectedItems.indexOf(item), 1);
    if (this.shouldReturnToAvailable(item)) {
      // add back to the master list
      this.masterItemsList.push(item);
      this.masterItemsList = this.sortItemsByName(this.masterItemsList);
    }
    // refresh reference so item-data component will detect changes
    this.itemsList = this.applyTypeFilter();
    this.refreshDisabledItems();
  }

  selectedItemType(item: CatalogItemUnion): string {
    if (!this.data.showTypeInSelectedList || !this.data.itemTypeField) {
      return '';
    }
    return item?.[this.data.itemTypeField] || '';
  }

  private shouldReturnToAvailable(item: CatalogItemUnion): boolean {
    return (item as { returnToAvailable?: boolean }).returnToAvailable !== false;
  }

  private compareByName(a: CatalogItemUnion, b: CatalogItemUnion): number {
    if (this.data.sortByName) {
      return (a.name || '').localeCompare(b.name || '', undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }

    return a.name > b.name ? 1 : -1;
  }

  private sortItemsByName(items: CatalogItemUnion[]): CatalogItemUnion[] {
    return items.slice().sort((a, b) => this.compareByName(a, b));
  }
}
